import { useRef, useState } from 'react';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Button } from '@hivebrite/design-system/Button';

import { useBuilderContext, useBuilderConfigContext } from 'hb-builder/hooks';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { ItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes';

const VIDEO_MAX_SIZE_GO = 1_000_000_000;
const ACCEPT_VIDEO_FILE = 'video/mp4,video/x-m4v,video/*';

export interface UploadVideoButtonProps {
  item: DocumentItem<ItemAttributes>;
  hasVideo: boolean;
  attributeType: string;
}

const UploadVideoButton = ({
  item,
  hasVideo,
  attributeType,
}: UploadVideoButtonProps) => {
  const { updateAttribute } = useBuilderContext();
  const {
    features: {
      image: { onUpload },
    },
  } = useBuilderConfigContext();

  const [errorVideo, setErrorVideo] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const onClick = () => {
    if (fileRef && fileRef.current) {
      fileRef.current.click();
    }
  };

  const handleInputFile = async (e) => {
    const [file] = e.target.files;

    if (onUpload && file?.size < VIDEO_MAX_SIZE_GO) {
      setIsUploadingVideo(true);
      setErrorVideo(false);
      const video = await onUpload(file);

      updateAttribute({
        id: item.id,
        attribute: attributeType,
        value: video,
      });
      setIsUploadingVideo(false);
    } else {
      return setErrorVideo(true);
    }
  };

  return (
    <>
      <Button
        onClick={onClick}
        variant="secondary"
        isLoading={!errorVideo && isUploadingVideo}
        dataTestId="btn-upload-video"
      >
        {hasVideo ? __('Change Video') : __('Upload Video')}
      </Button>
      <input
        type="file"
        id="file"
        ref={fileRef}
        style={{ display: 'none' }}
        onChange={handleInputFile}
        accept={ACCEPT_VIDEO_FILE}
      />
      {errorVideo && (
        <AdditionalInformation id="file-size" type="error">
          {__('Your video file is larger than 1GB')}
        </AdditionalInformation>
      )}
    </>
  );
};

export default UploadVideoButton;
